import React from "react";
/**
 * Per-route мета — императивно через DOM (без react-helmet-async).
 *
 * Почему не helmet: в этой сборке react-helmet-async НЕ применял теги в
 * production-бандле (первый клиентский flush не срабатывал; в dev «спасал»
 * двойной маунт StrictMode, в проде его нет → `<head>` оставался с дефолтами
 * из index.html). Императивный upsert детерминирован и не зависит от таймингов
 * библиотеки. См. docs/SEO.md.
 *
 * ВАЖНО: это клиентский рендер `<head>` — его подхватывает Google (исполняет
 * JS), но НЕ Яндекс и НЕ боты соцсетей. Для них работают статические мета в
 * `public/index.html`. Полноценные per-URL мета для Яндекса/соцсетей требуют
 * пререндера или серверной отдачи (см. SEO-план).
 *
 * canonical/og:url всегда указывают на ПРОД-домен (даже со staging), чтобы
 * тестовый хост не самоканоникализировался и не плодил дубли в индексе.
 */
const SITE_NAME = "Виртуоз";
export const PROD_ORIGIN = "https://virtuoz.pro";
const DEFAULT_TITLE = "Виртуоз — создать персональную песню в подарок онлайн";
/** Брендовая карточка 1200×630 для соцсетей, см. scripts/og-image.js. */
const DEFAULT_IMAGE = `${PROD_ORIGIN}/og-image.jpg`;
const DEFAULT_IMAGE_ALT = "Виртуоз — персональная песня в подарок за 5 минут";
/** Только эти хосты считаем прод-индексируемыми. */
export const PROD_HOSTS = ["virtuoz.pro", "www.virtuoz.pro"];
/** true только на прод-домене; на staging/localhost — false → noindex. */
export function isProdHost() {
    if (typeof window === "undefined")
        return true;
    // Пререндер (scripts/prerender.js) гоняет прод-сборку на 127.0.0.1, но снимок
    // отдаётся ботам с прод-домена. Без этой ветки в HTML для Яндекса уехал бы
    // noindex — ровно обратное тому, зачем пререндер делается.
    if (window.__PRERENDER__)
        return true;
    return PROD_HOSTS.includes(window.location.hostname);
}
/** Найти/создать <meta> по name|property и выставить content. */
export function upsertMeta(attr, key, content) {
    if (typeof document === "undefined")
        return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
/** Снять <meta> — нужно для размеров og:image, когда картинка не дефолтная. */
function removeMeta(attr, key) {
    if (typeof document === "undefined")
        return;
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el && el.parentNode)
        el.parentNode.removeChild(el);
}
function upsertLink(rel, href) {
    if (typeof document === "undefined")
        return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}
export function robotsContent(noindex) {
    if (!isProdHost())
        return "noindex, nofollow";
    return noindex ? "noindex, follow" : "index, follow";
}
function toAbsolute(pathOrUrl) {
    if (/^https?:\/\//i.test(pathOrUrl))
        return pathOrUrl;
    return `${PROD_ORIGIN}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}
export const Seo = ({ title, description, path, image, noindex = false, type = "website", jsonLd, }) => {
    const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";
    React.useEffect(() => {
        if (typeof document === "undefined")
            return;
        const pathname = path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
        const canonical = `${PROD_ORIGIN}${pathname}`;
        const fullTitle = title || DEFAULT_TITLE;
        const img = image ? toAbsolute(image) : DEFAULT_IMAGE;
        document.title = fullTitle;
        upsertMeta("name", "robots", robotsContent(noindex));
        upsertLink("canonical", canonical);
        upsertMeta("property", "og:site_name", SITE_NAME);
        upsertMeta("property", "og:locale", "ru_RU");
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:url", canonical);
        upsertMeta("property", "og:image", img);
        // Размеры отдаём только для брендовой карточки: у пользовательских
        // картинок (аватар, обложка подарка) они другие, а неверные width/height
        // ломают выбор большого превью в Telegram/VK сильнее, чем их отсутствие.
        if (img === DEFAULT_IMAGE) {
            upsertMeta("property", "og:image:type", "image/jpeg");
            upsertMeta("property", "og:image:width", "1200");
            upsertMeta("property", "og:image:height", "630");
            upsertMeta("property", "og:image:alt", DEFAULT_IMAGE_ALT);
            upsertMeta("name", "twitter:image:alt", DEFAULT_IMAGE_ALT);
        }
        else {
            removeMeta("property", "og:image:type");
            removeMeta("property", "og:image:width");
            removeMeta("property", "og:image:height");
            removeMeta("property", "og:image:alt");
            removeMeta("name", "twitter:image:alt");
        }
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", fullTitle);
        upsertMeta("name", "twitter:image", img);
        if (description) {
            upsertMeta("name", "description", description);
            upsertMeta("property", "og:description", description);
            upsertMeta("name", "twitter:description", description);
        }
        let ldEl = null;
        if (jsonLdStr) {
            ldEl = document.createElement("script");
            ldEl.type = "application/ld+json";
            ldEl.setAttribute("data-seo-jsonld", "");
            ldEl.textContent = jsonLdStr;
            document.head.appendChild(ldEl);
        }
        return () => {
            if (ldEl && ldEl.parentNode)
                ldEl.parentNode.removeChild(ldEl);
        };
    }, [title, description, path, image, noindex, type, jsonLdStr]);
    return null;
};
